export enum AppType {
  DASHBOARD = 'dashboard',
  ADMIN = 'admin',
}

export interface AppDetectionResult {
  appType: AppType
  isDashboard: boolean
  isAdmin: boolean
  hostname: string
  port: number | null
  isDevelopmentHostname: boolean
}

/**
 * Centralized function to detect which application (dashboard/admin) is currently running
 * This prevents code duplication and provides consistent app detection across the codebase
 */
export const detectCurrentApp = (): AppDetectionResult => {
  const hostname = window.location.hostname
  const port = window.location.port ? parseInt(window.location.port, 10) : null

  // Production environments - based on hostname
  const isAdminHostname = hostname.includes('admin')

  const isDevelopmentHostname =
    hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('dev')

  // Development environments - based on port and fallback
  let appType: AppType

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // In development, determine app type by port
    if (port === 5174) {
      // Admin app port
      appType = AppType.ADMIN
    } else if (port === 5173) {
      // Dashboard app port
      appType = AppType.DASHBOARD
    } else {
      // Fallback: check for URL patterns or default to dashboard
      const pathname = window.location.pathname

      if (
        pathname.includes('/dashboard/management/tenant') ||
        pathname.includes('/dashboard/management/reseller') ||
        pathname.includes('/dashboard/management/plan')
      ) {
        appType = AppType.ADMIN
      } else {
        // Default to dashboard for localhost
        appType = AppType.DASHBOARD
      }
    }
  } else if (isAdminHostname) {
    appType = AppType.ADMIN
  } else {
    // Default to dashboard for other hostnames
    appType = AppType.DASHBOARD
  }

  return {
    appType,
    isDashboard: appType === AppType.DASHBOARD,
    isAdmin: appType === AppType.ADMIN,
    hostname,
    port,
    isDevelopmentHostname,
  }
}

/**
 * Check if user role is allowed for current app
 */
export const isUserRoleAllowedForApp = (userRole: string, appType: AppType): boolean => {
  if (appType === AppType.ADMIN) {
    // Admin app: only allow SUPER_ADMIN and TENANT_ADMIN
    return userRole === 'SUPER_ADMIN' || userRole === 'TENANT_ADMIN'
  } else {
    // Dashboard app: allow TENANT_ADMIN and TENANT_USER (block SUPER_ADMIN)
    return userRole === 'TENANT_ADMIN' || userRole === 'TENANT_USER'
  }
}

/**
 * Get default redirect route based on user role and app type
 */
export const getDefaultRoute = (userRole: string, appType: AppType): string => {
  if (appType === AppType.ADMIN) {
    // Admin app routes
    if (userRole === 'SUPER_ADMIN') {
      return '/dashboard/management/tenants'
    } else if (userRole === 'TENANT_ADMIN') {
      return '/dashboard/management/users'
    } else {
      // Fallback
      return '/dashboard/management/tenants'
    }
  } else {
    // Dashboard app routes
    return '/dashboard'
  }
}

/**
 * Detect if the current device is iOS
 */
export const isIOSDevice = (): boolean => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  )
}

/**
 * Detect if the current device is Android
 */
export const isAndroidDevice = (): boolean => {
  return /Android/.test(navigator.userAgent)
}

/**
 * Get the appropriate App Store URL based on device
 * @param appId - The app ID (e.g., '389801252' or 'id389801252')
 * @param webUrl - Fallback web URL
 */
export const getAppStoreUrl = (appId: string, webUrl: string): string => {
  if (isIOSDevice()) {
    // Remove 'id' prefix if present
    const cleanAppId = appId.replace(/^id/, '')

    // Use itms-apps:// for iOS to open App Store app directly
    return `itms-apps://apps.apple.com/app/id${cleanAppId}`
  }

  return webUrl
}

/**
 * Get the appropriate Play Store URL based on device
 * @param packageId - The package ID (e.g., 'com.instagram.android')
 * @param webUrl - Fallback web URL
 */
export const getPlayStoreUrl = (packageId: string, webUrl: string): string => {
  if (isAndroidDevice()) {
    // Use market:// for Android to open Play Store app directly
    return `market://details?id=${packageId}`
  }

  return webUrl
}
